import React from "react";
import { useNavigate } from "react-router-dom";
import {
  TrophyIcon,
  ListBulletIcon,
  ChatBubbleBottomCenterIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";
import { formatNumber } from "../../../helpers/NumberFormats";
import ArtistMetadataCard from "./ArtistMetadataCard";
import { ArtistMetadata } from "../../../types/metadata";

interface OverviewTabProps {
  rapperData: {
    id: string;
    slug: string;
    name: string;
    imagePath: string | null;
    shortBio: string | null;
    stats: {
      communityRank: number | null;
      listAppearances: number;
      averageRank: number | null;
      totalReactions: number;
      totalComments: number;
      debateCount: number;
      points: number;
    };
    topLists: Array<{
      id: string;
      title: string;
      creator: string;
      creatorId: string;
      rank: number;
      reactions: number;
      comments: number;
    }>;
    debates: Array<{
      id: string;
      title: string;
      voteCount: number;
      isActive: boolean;
    }>;
  };
  metadata?: ArtistMetadata | null;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ rapperData, metadata }) => {
  const navigate = useNavigate();
  const topLists = rapperData.topLists.slice(0, 5); // Show top 5 lists
  const recentDebates = rapperData.debates.slice(0, 3); // Show top 3 debates

  const handleListClick = (listId: string) => {
    navigate(`/l/${listId}`);
  };

  const handleCreatorClick = (_creatorId: string, creatorName: string) => {
    navigate(`/u/${creatorName}`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Key Stats Summary */}
      <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrophyIcon className="w-6 h-6 text-yellow-500" />
          Key Statistics
        </h3>
        <div className="space-y-4">
          {rapperData.stats.communityRank && (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Community Rank</span>
              <span className="text-yellow-500 font-bold text-lg">
                #{rapperData.stats.communityRank}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Appears in Lists</span>
            <span className="text-white font-semibold">
              {formatNumber(rapperData.stats.listAppearances)}
            </span>
          </div>
          {rapperData.stats.averageRank && (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Average Position</span>
              <span className="text-white font-semibold">
                #{rapperData.stats.averageRank.toFixed(1)}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Total Points</span>
            <span className="text-white font-semibold">
              {formatNumber(rapperData.stats.points)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Debates</span>
            <span className="text-white font-semibold">
              {formatNumber(rapperData.stats.debateCount)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Discussion Comments</span>
            <span className="text-white font-semibold">
              {formatNumber(rapperData.stats.totalComments)}
            </span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <ChatBubbleBottomCenterIcon className="w-6 h-6 text-blue-500" />
          Recent Activity
        </h3>
        <div className="space-y-3 text-sm">
          <div className="text-gray-400">
            • Featured in {rapperData.stats.listAppearances} community lists
          </div>
          <div className="text-gray-400">
            • Participated in {rapperData.stats.debateCount} debates
          </div>
          <div className="text-gray-400">
            • Generated {rapperData.stats.totalComments} discussion comments
          </div>
          {rapperData.stats.communityRank && (
            <div className="text-gray-400">
              • Currently ranked #{rapperData.stats.communityRank} in community
            </div>
          )}
        </div>
      </div>

      {/* Top Lists */}
      {topLists.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <ListBulletIcon className="w-6 h-6 text-green-500" />
            Top Lists Featuring {rapperData.name}
          </h3>
          <div className="space-y-3">
            {topLists.map((list) => (
              <div
                key={list.id}
                className="flex items-center justify-between p-3 bg-gray-700/50 rounded-md hover:bg-gray-700 transition-colors"
              >
                <div className="flex-1">
                  <h4
                    onClick={() => handleListClick(list.id)}
                    className="text-white font-medium cursor-pointer hover:text-blue-400 transition-colors"
                  >
                    {list.title}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    by{" "}
                    <span
                      onClick={() =>
                        handleCreatorClick(list.creatorId, list.creator)
                      }
                      className="text-white font-medium cursor-pointer hover:text-blue-400 transition-colors"
                    >
                      {list.creator}
                    </span>
                  </p>
                </div>
                <div className="text-right text-sm">
                  <div className="text-yellow-500 font-bold">#{list.rank}</div>
                  <div className="text-gray-400 flex items-center gap-1">
                    <HeartIcon className="w-3 h-3" />
                    {list.reactions}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* {rapperData.topLists.length > 5 && (
            <button className="w-full mt-4 text-blue-400 hover:text-blue-300 text-sm font-medium">
              View All Lists ({rapperData.topLists.length})
            </button>
          )} */}
        </div>
      )}

      {/* Artist DNA — metadata card, spans full width when present */}
      {metadata && <ArtistMetadataCard metadata={metadata} />}

      {/* Active Debates */}
      {recentDebates.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <ChatBubbleBottomCenterIcon className="w-6 h-6 text-orange-500" />
            Active Debates
          </h3>
          <div className="space-y-3">
            {recentDebates.map((debate) => (
              <div
                key={debate.id}
                className="p-3 bg-gray-700/50 rounded-md hover:bg-gray-700 transition-colors"
              >
                <h4 className="text-white font-medium mb-1">{debate.title}</h4>
                <div className="flex items-center justify-between text-sm">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      debate.isActive
                        ? "bg-green-900 text-green-300"
                        : "bg-gray-900 text-gray-400"
                    }`}
                  >
                    {debate.isActive ? "Active" : "Closed"}
                  </span>
                  <span className="text-gray-400">
                    {formatNumber(debate.voteCount)} votes
                  </span>
                </div>
              </div>
            ))}
          </div>
          {rapperData.debates.length > 3 && (
            <button className="w-full mt-4 text-blue-400 hover:text-blue-300 text-sm font-medium">
              View All Debates ({rapperData.debates.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
